import Icon from "@ui-kit/icons/Icon.web";
import HeaderShareLinks, {
  HeaderShareLinksProps,
} from "@/components/molecules/headers/details-page/HeaderShareLinks";
import {
  getShareLinkDescription,
  getShareLinkUrl,
} from "@/components/molecules/headers/details-page/helpers";
import CartSummary from "@app-contexts/public/features/basket/components/cart-summary/CartSummary/CartSummary.web";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { SITE_URL } from "@shared/env";

import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { UserBasedTicketBasketHoldsSerializer } from "@app-contexts/user/features/basket/data/types/basket";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "next-i18next";
import { ReactElement, useMemo } from "react";

import {
  NestedPublicVenueSerializer,
  PublicBaseEventSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import moment from "moment-timezone";
import { useTimer } from "@ui-kit/feedback/timer/useTimer";
import DetailPageButton from "../../purchases/DetailPageButton";

interface HeaderActionProps {
  showText: boolean;
  onModalOpen: () => void;
  titleText: string;
  startsOnText?: string;
  locationText?: string;
  eventData?: PublicBaseEventSerializer;
  venue: NestedPublicVenueSerializer;
  membershipGroupData?: PublicMembershipGroupSerializer;
  holdDetailsData?: UserBasedTicketBasketHoldsSerializer;
  isAttractionPage?: boolean;
}

const HeaderAction = ({
  showText,
  onModalOpen,
  titleText,
  venue,
  startsOnText = undefined,
  locationText = undefined,
  eventData = undefined,
  membershipGroupData = undefined,
  holdDetailsData = null,
  isAttractionPage = false,
}: HeaderActionProps): ReactElement => {
  const { t } = useTranslation("purchases");

  const priceRangeString = useMemo(() => {
    let priceRange = "";

    if (eventData) {
      priceRange = eventData?.no_ticket_types
        ? t("Free")
        : getPriceRange({
            items: eventData?.ticket_types,
            allInPricing: Boolean(venue?.all_in_pricing_is_enabled),
            venueCurrency: venue?.currency,
            shouldRound: false,
          });
    } else if (membershipGroupData) {
      priceRange = getPriceRange({
        items: membershipGroupData?.membership_levels,
        allInPricing: Boolean(venue?.all_in_pricing_is_enabled),
        venueCurrency: venue?.currency,
        shouldRound: false,
      });
    }
    return priceRange;
  }, [eventData, membershipGroupData, venue, t]);

  const shareButtonsData: Omit<HeaderShareLinksProps, "title"> = useMemo(() => {
    if (eventData) {
      return {
        url: getShareLinkUrl(eventData),
        description: getShareLinkDescription(eventData),
      };
    }

    if (membershipGroupData) {
      return {
        url: getShareLinkUrl(membershipGroupData),
        description: getShareLinkDescription(membershipGroupData),
      };
    }

    return {
      url: SITE_URL,
      description: "",
    };
  }, [eventData, membershipGroupData]);

  const hidePriceText = !priceRangeString || isAttractionPage;
  const eventSaleStartsOn = eventData?.event_sale_starts_on;
  const saleStartsOnTz = moment
    .tz(eventSaleStartsOn, eventData?.timezone)
    .toDate();
  const [timeRemaining] = useTimer(saleStartsOnTz);

  const saleStartsOnPassed = eventSaleStartsOn
    ? new Date().valueOf() >= new Date(eventSaleStartsOn).valueOf()
    : true;

  return (
    <Flex
      boxShadow="md"
      bg="bg"
      direction="column"
      border="1px solid"
      borderColor="border"
      overflow="hidden"
      borderRadius="md"
    >
      <Flex
        p="0px 24px"
        h="element.4xl"
        borderBottom="1px solid"
        borderBottomColor="border"
        justifyContent="space-between"
        alignItems="center"
        fontSize="sm"
        data-testid="price-range"
      >
        <Text hidden={hidePriceText} color="fg">
          {t("Price")}
        </Text>
        <Text hidden={hidePriceText} fontWeight="bold" color="fg">
          {priceRangeString}
        </Text>
      </Flex>
      {isAttractionPage ? (
        <Flex p="xl" width="100%" direction="column">
          <CartSummary isDrawer={false} venue={venue} />
        </Flex>
      ) : (
        <Flex
          p="xl"
          width="100%"
          direction="column"
          data-testid="buy-button-container"
          hidden={eventData?.no_ticket_types}
          display={eventSaleStartsOn && timeRemaining > 0 ? "none" : "flex"}
        >
          <Flex direction="column" mb="lg" hidden={!showText}>
            <Text
              fontWeight="bold"
              color="fg"
              data-testid="title-on-scroll-down"
            >
              {titleText}
            </Text>
            <Flex
              hidden={!startsOnText}
              alignItems="center"
              mt="xs"
              fontSize="sm"
              data-testid="date-and-time-on-scroll-down"
            >
              <Icon.Calendar
                outline
                boxSize="element.md"
                mr="xs"
                color="fg.muted"
              />
              <Text color="fg.muted">{startsOnText}</Text>
            </Flex>
            <Flex
              hidden={!locationText}
              alignItems="center"
              mt="2xs"
              fontSize="sm"
              data-testid="location-on-scroll-down"
            >
              <Icon.Location
                outline
                boxSize="element.md"
                mr="xs"
                color="fg.muted"
              />
              <Text color="fg.muted">{locationText}</Text>
            </Flex>
          </Flex>

          <DetailPageButton
            saleStartsOnPassed={saleStartsOnPassed}
            eventData={eventData}
            membershipGroupData={membershipGroupData}
            holdDetailsData={holdDetailsData}
            openModal={onModalOpen}
            props={{
              "data-testid": "header-action-button",
              colorPalette: "secondary",
              minHeight: "40px",
              minWidth: "147px",
              display:
                eventSaleStartsOn && timeRemaining > 0 ? "none" : "block",
            }}
            data-testid="buy-button"
          />
        </Flex>
      )}
      <Flex
        justifyContent="center"
        alignItems="center"
        height="element.4xl"
        bg="bg.muted"
        borderTop="1px solid"
        borderTopColor="border"
        gap="4xl"
        data-testid="share-links-container"
      >
        <HeaderShareLinks
          {...shareButtonsData}
          title={
            <Text fontSize="md" color="fg.muted">
              {t("Share")}
            </Text>
          }
        />
      </Flex>
    </Flex>
  );
};

export default HeaderAction;
